import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { 
    Box, Typography, TextField, MenuItem, Paper, Grid, Button, 
    Chip, Pagination, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Avatar, IconButton, Tooltip 
} from "@mui/material";
import { 
    Plus, Wallet, Calendar, DollarSign, ChevronRight, 
    CheckCircle2, AlertCircle, ArrowUpDown, Receipt,
    Briefcase
} from "lucide-react";
import { agregarAdelanto } from "../../features/registroAdelantosSlice";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  lime: "#8FC440",
  yellow: "#E3E24F",
  pearl: "#EEF3F4",
  textGray: "#64748B"
};

const ListaAdelantos = () => {
    const { idPrograma } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [adelantos, setAdelantos] = useState([]);
    const [adelantosFiltrados, setAdelantosFiltrados] = useState([]);
    const [filtros, setFiltros] = useState({
        estado: '',
        ordenarPorFecha: 'desc'
    });
    const [paginaActual, setPaginaActual] = useState(1);
    const itemsPorPagina = 10;

    useEffect(() => {
        const obtenerAdelantos = async () => {
            const response = await fetch(
                `${apiUrl}/Adelanto/AdelantosPorPrograma?programaId=${idPrograma}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'apiKey': localStorage.getItem("apiKey")
                }
            });

            if (response.ok) {
                const data = await response.json();
                setAdelantos(data);
                dispatch(agregarAdelanto(data));
                setAdelantosFiltrados(data);
            }
        };
        obtenerAdelantos();
    }, [apiUrl, idPrograma, dispatch]);

    useEffect(() => {
        let filtrados = [...adelantos];

        if (filtros.estado) {
            filtrados = filtrados.filter(a =>
                a.estado.toLowerCase().includes(filtros.estado.toLowerCase())
            );
        }

        filtrados.sort((a, b) => {
            const dateA = new Date(a.fecha);
            const dateB = new Date(b.fecha);
            return filtros.ordenarPorFecha === 'asc' ? dateA - dateB : dateB - dateA;
        });

        setAdelantosFiltrados(filtrados);
    }, [filtros, adelantos]);

    const handleFiltroCambio = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
        setPaginaActual(1);
    };

    const totalPages = Math.ceil(adelantosFiltrados.length / itemsPorPagina);
    const adelantosPagidados = adelantosFiltrados.slice((paginaActual - 1) * itemsPorPagina, paginaActual * itemsPorPagina);

    return (
        <Box sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 14 }, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
            
            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: PALETTE.dark, width: 50, height: 50 }}>
                        <Briefcase size={24} color="white" />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark }}>Adelantos del Programa</Typography>
                        <Typography variant="body2" sx={{ color: PALETTE.azur, fontWeight: 600 }}>Historial y seguimiento de fondos</Typography>
                    </Box>
                </Box>

                {localStorage.getItem("rol") === "coordinador" && (
                    <Button 
                        variant="contained" 
                        startIcon={<Plus size={20} />}
                        onClick={() => navigate("/FormularioAdelanto")}
                        sx={{ 
                            borderRadius: '14px', px: 3, py: 1.2, textTransform: 'none', fontWeight: 800,
                            bgcolor: PALETTE.dark, '&:hover': { bgcolor: PALETTE.teal }
                        }}
                    >
                        Solicitar Adelanto
                    </Button>
                )}
            </Box>

            {/* FILTROS SIMPLIFICADOS */}
            <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: '24px', backgroundColor: PALETTE.pearl, border: `1px solid ${PALETTE.sky}` }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField 
                            select fullWidth size="small" name="estado" label="Filtrar por Estado"
                            value={filtros.estado} onChange={handleFiltroCambio}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        >
                            <MenuItem value="">Todos los estados</MenuItem>
                            <MenuItem value="Pendiente">Pendiente</MenuItem>
                            <MenuItem value="Aprobado">Aprobado</MenuItem>
                            <MenuItem value="Desaprobado">Desaprobado</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField 
                            select fullWidth size="small" name="ordenarPorFecha" label="Ordenar"
                            value={filtros.ordenarPorFecha} onChange={handleFiltroCambio}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                            InputProps={{ startAdornment: <ArrowUpDown size={16} style={{marginRight: 8, color: PALETTE.teal}} /> }}
                        >
                            <MenuItem value="desc">Más recientes primero</MenuItem>
                            <MenuItem value="asc">Más antiguos primero</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* TABLA CORPORATIVA */}
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '24px', border: '1px solid #eee' }}>
                <Table sx={{ minWidth: 1000 }}>
                    <TableHead sx={{ bgcolor: '#fbfcfc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>DETALLES DEL ADELANTO</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>FECHA / CC</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>IMPORTE TOTAL</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>ESTADO FONDO</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>RENDICIÓN</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adelantosPagidados.map(a => (
                            <TableRow key={a.id} hover>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: PALETTE.dark }}>{a.nombre}</Typography>
                                    <Tooltip title={a.motivo}>
                                        <Typography variant="caption" sx={{ color: PALETTE.textGray, display: 'block', maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {a.motivo}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <Calendar size={14} color={PALETTE.teal} />
                                        <Typography variant="caption" fontWeight={700}>
                                            {new Date(a.fecha).toLocaleDateString('es-ES')}
                                        </Typography>
                                    </Box>
                                    <Chip label={`CC: ${a.centroCosto}`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: PALETTE.ice }} />
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 900, color: PALETTE.dark }}>
                                            {a.moneda === "USD" ? "U$S" : "$"} {Number(a.importe).toLocaleString()}
                                        </Typography>
                                       
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={a.estado} 
                                        size="small"
                                        sx={{ 
                                            fontWeight: 800, borderRadius: '8px',
                                            bgcolor: a.estado === "Aprobado" ? `${PALETTE.lime}15` : a.estado === "Desaprobado" ? "#f65f4e15" : `${PALETTE.textGray}15`,
                                            color: a.estado === "Aprobado" ? PALETTE.lime : a.estado === "Desaprobado" ? "#f65f4e" : PALETTE.textGray,
                                            border: `1px solid ${a.estado === "Aprobado" ? PALETTE.lime : a.estado === "Desaprobado" ? "#f65f4e" : PALETTE.textGray}`
                                        }} 
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={a?.laRendicion?.rendicioUsuario && a?.laRendicion?.rendicionFinanzas ? "Terminada" : "Pendiente"}
                                        size="small"
                                        variant="outlined"
                                        icon={a?.laRendicion?.rendicioUsuario && a?.laRendicion?.rendicionFinanzas ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                                        sx={{ 
                                            fontWeight: 800, borderRadius: '8px',
                                            borderColor: a?.laRendicion?.rendicioUsuario && a?.laRendicion?.rendicionFinanzas ? PALETTE.lime : PALETTE.yellow,
                                            color: a?.laRendicion?.rendicionFinanzas ? PALETTE.lime : "#9A8D00"
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton 
                                        onClick={() => navigate('/adelanto/' + a.id)}
                                        sx={{ color: PALETTE.dark, '&:hover': { bgcolor: PALETTE.ice, color: PALETTE.teal } }}
                                    >
                                        <ChevronRight size={22} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {adelantosPagidados.length === 0 && (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Receipt size={40} color="#ccc" style={{ marginBottom: 8 }} />
                        <Typography variant="body1" color="text.secondary">Este programa no tiene adelantos registrados.</Typography>
                    </Box>
                )}
            </TableContainer>

            {/* PAGINACIÓN */}
            {totalPages > 1 && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Pagination 
                        count={totalPages} page={paginaActual} onChange={(e, v) => setPaginaActual(v)}
                        sx={{ 
                            '& .MuiPaginationItem-root': { borderRadius: '12px', fontWeight: 700 },
                            '& .Mui-selected': { bgcolor: `${PALETTE.dark} !important`, color: 'white' } 
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ListaAdelantos;